import React, { useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import StickyMobileCta from './components/StickyMobileCta';
import ScrollToTop from './components/ScrollToTop';
import BrandLoader from './components/BrandLoader';
import ScrollProgress from './components/ScrollProgress';
import SEO from './components/SEO';
import TrailingSlashRedirect from './components/TrailingSlashRedirect';
import { track } from './utils/analytics';

import Home from './pages/Home';
import WeightLoss from './pages/WeightLoss';
import HowItWorks from './pages/HowItWorks';
import Packages from './pages/Packages';
import Features from './pages/Features';
import WhyUs from './pages/WhyUs';
import About from './pages/About';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';
import Intake from './pages/Intake';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Safety from './pages/Safety';
import SensitiveDataConsent from './pages/SensitiveDataConsent';
import PreventiveHealth from './pages/PreventiveHealth';
import PreventiveHealthCn from './pages/PreventiveHealthCn';
import Clinics from './pages/Clinics';
import TestimonialsPage from './pages/TestimonialsPage';
import MedicalDisclaimer from './pages/MedicalDisclaimer';
import NotFound from './pages/NotFound';

// Routes that render as a focused funnel — no marketing chrome.
const CHROMELESS = ['/intake'];

function Chrome({ children }) {
  const { pathname } = useLocation();
  const chromeless = CHROMELESS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  if (chromeless) {
    return <main key={pathname} className="min-h-screen page-fade">{children}</main>;
  }
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main key={pathname} className="flex-1 page-fade">{children}</main>
      <Footer />
      <StickyMobileCta />
    </div>
  );
}

// Fire page_view on every route change. Path only — no health PII.
function PageViewTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    track('page_view', { page_path: pathname });
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <>
      <BrandLoader />
      <ScrollProgress />
      <ScrollToTop />
      <TrailingSlashRedirect />
      <PageViewTracker />
      {/* Baseline SEO — pulls title/meta/canonical from src/utils/seoConfig.js
          for the current route. Individual pages can render <SEO ... /> again
          to attach page-specific JSON-LD or override defaults. */}
      <SEO />
      <Chrome>
        <Routes>
          <Route path="/"                     element={<Home />} />
          {/* Weight-management landing (SEO Master §4). /weight-loss is kept
              as a redirect alias so old backlinks still resolve. */}
          <Route path="/weight-management"    element={<WeightLoss />} />
          <Route path="/weight-loss"          element={<Navigate to="/weight-management" replace />} />
          <Route path="/how-it-works"         element={<HowItWorks />} />
          <Route path="/clinics"              element={<Clinics />} />
          <Route path="/packages"             element={<Packages />} />
          <Route path="/features"             element={<Features />} />
          <Route path="/why-us"               element={<WhyUs />} />
          <Route path="/about"                element={<About />} />
          <Route path="/faq"                  element={<FAQ />} />
          <Route path="/testimonials"         element={<TestimonialsPage />} />
          <Route path="/contact"              element={<Contact />} />
          <Route path="/intake"               element={<Intake />} />
          <Route path="/terms"                element={<Terms />} />
          <Route path="/privacy"              element={<Privacy />} />
          {/* /privacy-policy is the SEO-canonical alias per the master doc. */}
          <Route path="/privacy-policy"       element={<Privacy />} />
          <Route path="/medical-disclaimer"   element={<MedicalDisclaimer />} />
          <Route path="/safety"               element={<Safety />} />
          <Route path="/sensitive-data-consent" element={<SensitiveDataConsent />} />
          <Route path="/preventive-health"          element={<PreventiveHealth />} />
          <Route path="/preventive-health/waitlist" element={<PreventiveHealth />} />
          {/* <Route path="/preventive-health-cn"   element={<PreventiveHealthCn />} /> */}
          <Route path="*"                     element={<NotFound />} />
        </Routes>
      </Chrome>
    </>
  );
}
